import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { fmtDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { NL } from "@/constants/testIds";
import { Mail, MessageSquare, Send, StickyNote, Phone } from "lucide-react";
import { toast } from "sonner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Link } from "react-router-dom";

const SEGMENTS = [
  ["tutti", "Tutti i donatori"],
  ["attivi", "Donatori attivi (180gg)"],
  ["chiese", "Chiese"],
  ["aziende", "Aziende"],
  ["privati", "Privati"],
  ["inattivi", "Inattivi (180gg+)"],
  ["top_donor", "Top donor YTD"],
  ["nuovi_90_giorni", "Nuovi (90gg)"],
];

const CHANNEL_ICON = {
  email: Mail, whatsapp: MessageSquare, telefono: Phone, nota: StickyNote,
};

export default function Communications() {
  const qc = useQueryClient();
  const { data: comms = [] } = useQuery({
    queryKey: ["communications-all"],
    queryFn: async () => (await api.get("/communications")).data,
  });
  const { data: newsletters = [] } = useQuery({
    queryKey: ["newsletters"],
    queryFn: async () => (await api.get("/newsletters")).data,
  });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "", subject: "", segment: "tutti", send_now: false,
    html_content: "<h2>Cari amici di RDM ClassRoom,</h2>\n<p>Grazie per il vostro sostegno. Ecco un aggiornamento sulle nostre attività recenti…</p>",
  });
  const [segCount, setSegCount] = useState(null);

  async function previewSegment(segment) {
    try {
      const { data } = await api.get(`/newsletters/segments/${segment}/preview`);
      setSegCount(data.count);
    } catch { setSegCount(null); }
  }
  React.useEffect(() => { previewSegment(form.segment); }, [form.segment]);

  const create = useMutation({
    mutationFn: async () => (await api.post("/newsletters", form)).data,
    onSuccess: (data) => {
      toast.success(form.send_now ? `Newsletter inviata a ${data.sent_count} destinatari` : "Newsletter salvata come bozza");
      setOpen(false);
      qc.invalidateQueries({ queryKey: ["newsletters"] });
      qc.invalidateQueries({ queryKey: ["communications-all"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1400px] mx-auto">
      <PageHeader
        title="Comunicazioni"
        subtitle="Storico contatti e newsletter periodiche ai donatori."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid={NL.createBtn} className="rounded-full bg-[#E2725B] hover:bg-[#c85d47] text-white">
                <Send size={14} className="mr-1.5"/> Nuova newsletter
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle>Nuova newsletter</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Nome interno</Label>
                    <Input data-testid={NL.name} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5"/>
                  </div>
                  <div>
                    <Label>Segmento destinatari</Label>
                    <Select value={form.segment} onValueChange={(v) => setForm({ ...form, segment: v })}>
                      <SelectTrigger data-testid={NL.segment} className="mt-1.5"><SelectValue/></SelectTrigger>
                      <SelectContent>
                        {SEGMENTS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {segCount != null && <div className="text-[11px] text-stone-500 mt-1">Destinatari stimati: {segCount}</div>}
                  </div>
                </div>
                <div>
                  <Label>Oggetto</Label>
                  <Input data-testid={NL.subject} value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="mt-1.5"/>
                </div>
                <div>
                  <Label>Contenuto HTML</Label>
                  <Textarea data-testid={NL.html} rows={10} value={form.html_content} onChange={(e) => setForm({ ...form, html_content: e.target.value })} className="mt-1.5 mono text-xs"/>
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox data-testid={NL.sendNow} checked={form.send_now} onCheckedChange={(v) => setForm({ ...form, send_now: !!v })}/>
                  Invia subito (richiede BREVO_API_KEY configurata; altrimenti verrà solo salvata come registrata)
                </label>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
                <Button data-testid={NL.saveBtn} className="bg-[#1C352D] hover:bg-[#264A3E]" onClick={() => create.mutate()}>
                  {form.send_now ? "Invia ora" : "Salva bozza"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <Tabs defaultValue="storico">
        <TabsList className="bg-white border border-stone-200">
          <TabsTrigger value="storico">Storico contatti</TabsTrigger>
          <TabsTrigger value="newsletter">Newsletter</TabsTrigger>
        </TabsList>

        <TabsContent value="storico" className="mt-4">
          <Card className="border border-stone-200 rounded-xl shadow-none">
            <Table>
              <TableHeader>
                <TableRow className="bg-stone-50/60 hover:bg-stone-50/60">
                  <TableHead className="uppercase text-[10px] tracking-widest">Quando</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-widest">Canale</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-widest">Donatore</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-widest">Oggetto/Nota</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-widest">Stato</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {comms.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-10 text-stone-400">Nessuna comunicazione registrata.</TableCell></TableRow>}
                {comms.slice(0, 200).map((c, i) => {
                  const Ic = CHANNEL_ICON[c.channel] || StickyNote;
                  return (
                    <TableRow key={c.id} className="animate-in-row" style={{ animationDelay: `${i * 4}ms` }}>
                      <TableCell className="text-sm">{fmtDate(c.sent_at)}</TableCell>
                      <TableCell><span className="inline-flex items-center gap-1.5 text-xs capitalize"><Ic size={12}/> {c.channel}</span></TableCell>
                      <TableCell><Link to={`/donatori/${c.donor_id}`} className="text-sm hover:underline">apri</Link></TableCell>
                      <TableCell className="text-sm">{c.subject || <span className="text-stone-400">—</span>}</TableCell>
                      <TableCell className="text-xs text-stone-600 capitalize">{c.status}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="newsletter" className="mt-4">
          <Card className="border border-stone-200 rounded-xl shadow-none">
            <Table>
              <TableHeader>
                <TableRow className="bg-stone-50/60 hover:bg-stone-50/60">
                  <TableHead className="uppercase text-[10px] tracking-widest">Nome</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-widest">Oggetto</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-widest">Segmento</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-widest">Inviata</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-widest text-right">Destinatari</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-widest">Stato</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {newsletters.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-10 text-stone-400">Nessuna newsletter.</TableCell></TableRow>}
                {newsletters.map((n) => (
                  <TableRow key={n.id}>
                    <TableCell className="font-medium">{n.name}</TableCell>
                    <TableCell className="text-sm">{n.subject}</TableCell>
                    <TableCell className="text-xs">{n.segment}</TableCell>
                    <TableCell className="text-sm">{fmtDate(n.sent_at) || "—"}</TableCell>
                    <TableCell className="text-right font-semibold">{n.sent_count}</TableCell>
                    <TableCell className="text-xs capitalize">{n.status}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
